import React, { useEffect, useState } from 'react';

const RangeFieldType = ({
    value,
    start: startProp,
    end: endProp,
    stops: stopsProp,
    measurement = '',
    className = '',
    color = '#111827',
}) => {
    const [hoveredStop, setHoveredStop] = useState(null);

    // Normalize the many shapes callers pass in:
    //  - array shape  [start, ...stops, end]  (RangeCreator / hydrated value)
    //  - object shape { startValue, endValue, stops: [{ value }] }
    //  - legacy separate start / end / stops props
    let start, end, stops;
    if (Array.isArray(value)) {
        start = value[0];
        end = value[value.length - 1];
        stops = value.slice(1, -1);
    } else if (value && typeof value === 'object') {
        start = value.startValue;
        end = value.endValue;
        stops = Array.isArray(value.stops)
            ? value.stops.map((s) => (s && typeof s === 'object' ? s.value : s))
            : [];
    } else {
        start = startProp;
        end = endProp;
        stops = stopsProp;
    }

    // Ensure stops is always an array
    const stopValues = Array.isArray(stops) ? stops : [];
    const hasStops = stopValues.length > 0;

    // Format number to avoid unnecessary decimals
    const formatValue = (value) => {
        // A half-filled range used to print the string "NaN" at both ends, and
        // Number(null) is 0, so a missing bound would read as a real zero.
        if (value === null || value === undefined || value === '') return '—';
        const num = Number(value);
        if (!Number.isFinite(num)) return '—';
        return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    };

    // A degenerate range (equal ends, or an end that never got filled in) makes
    // this division Infinity or NaN, and "left: NaN%" throws every marker into
    // the top-left corner. There is no meaningful position to give them, so
    // they are not drawn.
    const bounded = ![start, end].some((bound) => bound === null || bound === undefined || bound === '');
    const span = Number(end) - Number(start);
    const positionable = bounded && Number.isFinite(span) && span !== 0;
    const stopPositions = positionable
        ? stopValues.map((value, index) => ({
            value,
            index,
            position: ((Number(value) - Number(start)) / span) * 100
        })).filter(({ position }) => Number.isFinite(position))
        : [];

    // The stop values used to exist only as a mouse-hover popover, so keyboard
    // and screen-reader users had no way to read them at all. They are now
    // announced as one sentence (below) and each marker is a focusable button
    // that reveals its own value — WCAG 1.3.1 Info and Relationships and
    // 2.1.1 Keyboard.
    const unit = measurement ? ` ${measurement}` : '';
    const summary = [
        `Range from ${formatValue(start)}${unit} to ${formatValue(end)}${unit}.`,
        hasStops
            ? `Marks at ${stopValues.map((value) => `${formatValue(value)}${unit}`).join(', ')}.`
            : null,
    ].filter(Boolean).join(' ');

    // Content revealed on hover or focus must be dismissable without moving the
    // pointer or the focus (WCAG 1.4.13 Content on Hover or Focus).
    useEffect(() => {
        if (hoveredStop === null) return;
        const onKeyDown = (event) => { if (event.key === 'Escape') setHoveredStop(null); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [hoveredStop]);

    return (
        <div className={`space-y-2 ${className}`}>
            <p className="sr-only">{summary}</p>

            {/* Range values */}
            <div aria-hidden="true" className="flex items-center justify-between text-sm text-gray-600">
                <span className="font-medium">{formatValue(start)} {measurement}</span>
                <span className="font-medium">{formatValue(end)} {measurement}</span>
            </div>

            {/* Range bar with stops */}
            <div className="relative">
                <div className="h-2 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full rounded-full" style={{ width: '100%', backgroundColor: color }}></div>
                </div>

                {/* Stop markers and labels */}
                {stopPositions.map(({ value, index, position }) => {
                    const clampedPosition = Math.max(0, Math.min(100, position));

                    return (
                        <div key={index} className="absolute top-0" style={{ left: `${clampedPosition}%` }}>
                            <button
                                type="button"
                                aria-label={`Mark at ${formatValue(value)}${unit}`}
                                aria-expanded={hoveredStop === index}
                                onMouseEnter={() => setHoveredStop(index)}
                                onMouseLeave={() => setHoveredStop(null)}
                                onFocus={() => setHoveredStop(index)}
                                onBlur={() => setHoveredStop(null)}
                                onClick={() => setHoveredStop((current) => (current === index ? null : index))}
                                className="relative block w-0.5 h-2 -translate-x-1/2 bg-white cursor-pointer before:absolute before:-inset-x-2.5 before:-inset-y-2.5 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-900"
                            >
                                {/* Popover */}
                                {hoveredStop === index && (
                                    <span className="absolute z-10 block px-2 py-1 mb-1 -translate-x-1/2 border shadow text-card-foreground bg-card rounded-xl bottom-full left-1/2 whitespace-nowrap">
                                        {formatValue(value)} {measurement}
                                        <span className="absolute block w-0 h-0 -translate-x-1/2 border-t-2 border-l-2 border-r-2 top-full left-1/2 border-l-transparent border-r-transparent border-t-card"></span>
                                    </span>
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RangeFieldType;
